<template>
  <div class="version-history-page">
    <backbutton/>
    <div class="container">
      <var-pull-refresh v-model="refreshing" @refresh="loadHistory">
        <var-list
          :finished="finished"
          @load="loadHistory"
          class="list-wrap"
        >
          <var-cell 
            v-for="item in historyList" 
            :key="item.version" 
            class="version-card"
            border
            @click="handleSelect(item)"
          >
            <div class="card-content">
              <div class="header">
                <span class="version">V {{ item.version }}</span>
                <var-chip 
                  v-if="currentVersion === item.version" 
                  type="success" 
                  size="mini"
                  class="current-tag"
                >
                  {{ $t('hotUpdate.currentVersionhis')}}
                </var-chip>
              </div>
              <div class="time">{{ item.updateTime }}</div>
              <div v-if="item.description" class="desc">{{ item.description }}</div>
            </div>
            <template #extra>
              <var-icon name="chevron-right" />
            </template>
          </var-cell>
        </var-list>
      </var-pull-refresh>
    </div>

    <!-- 确认切换弹窗 -->
    <var-dialog
      v-model:show="showDialog"
      :title="$t('hotUpdate.confirmSwitchTitle')"
      @confirm="confirmSwitch"
    >
      <div class="dialog-body">
        <p>{{ $t('hotUpdate.confirmSwitchMsg', { version: selectedItem?.version }) }}</p>
        <p class="warning">{{ $t('hotUpdate.restartWarning') }}</p>
      </div>
    </var-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { hotUpdateManager, VersionInfo } from './hotupdate';
import { toast } from 'd3components/src/utils/tools';
import Backbutton from '@/layout/components/backbutton.vue';

const router = useRouter();
const historyList = ref<VersionInfo[]>([]);
const currentVersion = ref('');
const refreshing = ref(false);
const finished = ref(false);
const showDialog = ref(false);
const selectedItem = ref<VersionInfo | null>(null);

// 加载版本列表
const loadHistory = async () => {
  try {
    const [list, current] = await Promise.all([
      hotUpdateManager.fetchHistory(),
      hotUpdateManager.getDisplayedVersion()
    ]);
    historyList.value = list;
    currentVersion.value = current;
    finished.value = true;
  } catch (error) {
    toast('获取版本历史失败');
  } finally {
    refreshing.value = false;
  }
};

// 选择版本
const handleSelect = (item: VersionInfo) => {
  if (item.version === currentVersion.value) {
    toast('已是当前运行版本');
    return;
  }
  selectedItem.value = item;
  showDialog.value = true;
};

// 执行切换
const confirmSwitch = async () => {
  if (!selectedItem.value) return;
  toast('正在启动更新...');
  await hotUpdateManager.switchToVersion(selectedItem.value);
};

onMounted(loadHistory);
</script>

<style scoped lang="scss">
.version-history-page {
  min-height: 100vh;
  background-color: var(--color-body);
  color: var(--color-text);
  .container {
    padding: 12px;
  }

  .version-card {
    margin-bottom: 12px;
    background: var(--color-surface-container-highest, #fff);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .card-content {
      padding: 4px 0;
    }

    .header {
      display: flex;
      align-items: center;
      margin-bottom: 6px;

      .version {
        font-size: 17px;
        font-weight: bold;
      }

      .current-tag {
        margin-left: 10px;
      }
    }

    .time {
      font-size: 12px;
      color: var(--color-text-disabled, #999);
      margin-bottom: 6px;
    }

    .desc {
      font-size: 14px;
      color: var(--color-text-secondary, #666);
      line-height: 1.5;
    }
  }

  .dialog-body {
    padding: 16px 0;
    text-align: center;
    
    .warning {
      margin-top: 8px;
      font-size: 13px;
      color: var(--color-danger, #f44336);
    }
  }
}

/* 黑暗模式适配（如果全局变量未生效的兜底） */
:root[var-theme-dark] .version-card {
  background: #1e1e1e;
  .version { color: #fff; }
  .desc { color: #bbb; }
}
</style>
